import React from "react";

import { SortDirection } from "@tanstack/react-table";
import { cva } from "class-variance-authority";

import { PendoProps } from "../../utils/pendo";

import { CellWidth } from "./Cell";
import * as theme from "./HeadCell.css";
import { Sorter } from "./Sorter";

const headCellVariants = cva([theme.cell, theme.headCell], {
    variants: {
        sortable: {
            true: theme.sortableHeadCell,
        },
        pinned: {
            true: theme.cellPinned,
        },
    },
});

export interface HeadCellProps extends PendoProps {
    children: React.ReactNode;
    isSorted?: SortDirection | false;
    onClick?: () => void;
    canSort?: boolean;
    width?: CellWidth;
    isPinned?: boolean;
    left?: number;
}
export function HeadCell({
    children,
    isSorted,
    onClick,
    width,
    isPinned,
    left,
    canSort = false,
    "data-pendo": dataPendo,
}: HeadCellProps) {
    const ariaSort = canSort
        ? (ARIA_SORT_MAP[String(isSorted)] ?? "none")
        : undefined;
    return (
        <th
            aria-sort={ariaSort}
            data-pendo={dataPendo}
            className={headCellVariants({
                sortable: canSort,
                pinned: isPinned,
            })}
            style={{
                width: width ?? "auto",
                left: left ?? 0,
            }}
        >
            <div
                className={theme.headCellContent}
                role={canSort ? "button" : undefined}
                tabIndex={canSort ? 0 : undefined}
                onClick={canSort ? onClick : undefined}
                onKeyDown={
                    canSort
                        ? (event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  onClick?.();
                              }
                          }
                        : undefined
                }
            >
                <div className={theme.headCellLabel}>{children}</div>
                {canSort && isSorted !== undefined && (
                    <Sorter sortDirection={isSorted} />
                )}
            </div>
        </th>
    );
}

const ARIA_SORT_MAP: Record<string, "ascending" | "descending" | "none"> = {
    asc: "ascending",
    desc: "descending",
};
